/**
 * Input with icon on the right side
 * Supports password visibility toggle
 */

import { Input } from '@shared/components/ui/input'
import { cn } from '@shared/lib/utils'
import type { LucideIcon } from 'lucide-react'
import { Eye, EyeOff } from 'lucide-react'
import { forwardRef, useState } from 'react'

interface InputWithIconProps extends React.InputHTMLAttributes<HTMLInputElement> {
  icon?: LucideIcon
  isPasswordToggle?: boolean
}

export const InputWithIcon = forwardRef<HTMLInputElement, InputWithIconProps>(
  ({ icon: Icon, isPasswordToggle, type, className, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false)

    const inputType = isPasswordToggle
      ? (showPassword ? 'text' : 'password')
      : type

    const IconComponent = isPasswordToggle
      ? (showPassword ? EyeOff : Eye)
      : Icon

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={inputType}
          className={cn('pr-10', className)}
          {...props}
        />
        {IconComponent && (
          <button
            type="button"
            onClick={() => isPasswordToggle && setShowPassword(!showPassword)}
            className={cn(
              'absolute right-3 top-1/2 -translate-y-1/2',
              'text-muted-foreground hover:text-foreground',
              'transition-colors',
              isPasswordToggle && 'cursor-pointer'
            )}
            disabled={!isPasswordToggle}
            tabIndex={isPasswordToggle ? 0 : -1}
          >
            <IconComponent className="h-5 w-5" />
          </button>
        )}
      </div>
    )
  }
)

InputWithIcon.displayName = 'InputWithIcon'
